import type { IRegion, IRegionCountry } from '@/pages/intelligence/types/common';

export const handleCountriesData2Standard = (data: any[]): IRegionCountry[] => {
  if (!Array.isArray(data)) return [];
  return data.map((item) => {
    const { abbre, name, country_cn: countryCN } = item || {};
    return {
      country_code: abbre || '',
      country_name_en: name || '',
      country_name_ch: countryCN || '',
    };
  });
};

export const handleRegionsCountriesData2Standard = (data: any[]): IRegion[] => {
  if (!Array.isArray(data)) return [];
  return data.map((item) => {
    const { continent, subcontinent, subcontinent_cn: subcontinentCN, country_set: countrySet } = item ?? {};
    const countries: IRegionCountry[] = handleCountriesData2Standard(countrySet);
    return {
      area_code: continent || '',
      area_name_en: subcontinent || '',
      area_name_ch: subcontinentCN || '',
      country_set: countries || [],
    };
  });
};

export const handleRegionsCountriesData2StandardNew = (list?: any[] | null): IRegion[] => {
  if (!Array.isArray(list) || list.length === 0) {
    return [];
  }

  return list.map((item) => {
    const { region, continent, subcontinent, subcontinent_cn: subcontinentCN, country_set: countrySet } = item ?? {};
    const countries: IRegionCountry[] = handleCountriesData2Standard(countrySet);
    return {
      area_code: region || continent || '',
      area_name_en: subcontinent || '',
      area_name_ch: subcontinentCN || '',
      country_set: countries || [],
    };
  });
};

export const getAllCountries = (regions: IRegion[]): string[] => {
  if (!Array.isArray(regions)) return [];
  const result: string[] = [];
  for (const region of regions) {
    const countries = region?.country_set;
    if (!Array.isArray(countries)) continue;
    for (const country of countries) {
      const countryCode = country?.country_code;
      if (countryCode) {
        result.push(countryCode);
      }
    }
  }
  return result;
};

/**
 * 单国家模式，获取有效的勾选国家，优先级按照country当前勾选国家 > defaultCountry指定的国家 > countryCodes当前国家选项列表第一个国家获取
 * @param country 当前勾选的国家
 * @param countryCodes 当前国家选项列表
 * @param defaultCountry 默认勾选国家，默认为us
 * @return 有效的勾选国家
 */
export const getValidCountry = (country: string, countryCodes: string[], defaultCountry = 'us'): string => {
  if (!Array.isArray(countryCodes)) return '';
  if (countryCodes.includes(country)) return country;
  if (countryCodes.includes(defaultCountry)) return defaultCountry;
  return countryCodes[0] || '';
};

/**
 * 多国家模式时，获取有效的勾选国家列表
 * @param countries 当前勾选的国家列表
 * @param countryCodes 当前国家选项列表
 * @return 有效的勾选国家列表
 */
const getValidCountries = (countries: string[], countryCodes: string[]): string[] => {
  if (!Array.isArray(countryCodes)) return [];
  if (!Array.isArray(countries)) return countryCodes;
  const validCountries = countries.filter((country) => countryCodes.includes(country));
  return validCountries.length ? validCountries : countryCodes;
};

/**
 * 判断当前勾选国家列表是否为全选
 * @param countries 当前勾选国家列表
 * @param countryCodes 当前国家选项列表
 */
const getIsAllCountriesChecked = (countries: string[], countryCodes: string[]): boolean => {
  if (!Array.isArray(countryCodes)) return true;
  if (!Array.isArray(countries)) return true;
  return countries.length === countryCodes.length && countries.every((country) => countryCodes.includes(country));
};

/**
 * 获取有效勾选国家列表（国家选项列表更新后）
 * @param options 其他参数
 * @param options.type 当前类型，单国家、多国家
 * @param options.countries 当前勾选国家列表
 * @param options.regions 当前国家选项列表
 * @param options.nextRegions 更新后的国家选项列表
 */
export const getValidCountriesWhenRegionsOptionsUpdate = (options: {
  type: 'single' | 'multiple';
  countries: string[];
  regions: IRegion[];
  nextRegions: IRegion[];
}): string[] => {
  const { type, countries, regions, nextRegions } = options || {};
  const nextAllCountries = getAllCountries(nextRegions);
  let countriesTmp: string[];
  if (type === 'multiple') {
    const allCountries = getAllCountries(regions);
    if (getIsAllCountriesChecked(countries, allCountries)) {
      countriesTmp = nextAllCountries;
    } else {
      countriesTmp = getValidCountries(countries, nextAllCountries);
    }
  } else {
    const validCountry = getValidCountry(countries?.[0], nextAllCountries);
    countriesTmp = validCountry ? [validCountry] : [];
  }
  return countriesTmp;
};
